/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Button, INodeProps } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';

export interface IAddContactInfoInput {
    inputRef?: React.RefObject<HTMLInputElement>;
    customerRequisitionInputRef?: React.RefObject<HTMLInputElement>;
    customerReferenceInputRef?: React.RefObject<HTMLInputElement>;
    customerMessageInputRef?: React.RefObject<HTMLTextAreaElement>;
    hasError: boolean;
    baseClassName: string;
    email: string;
    customerRequisition: string;
    customerRequisitionLocked: boolean;
    customerReference: string;
    customerReferenceLocked: boolean;
    customerMessage: string;
    resources: {
        emailLabel: string;
        emailErrortext: string;
        saveBtnLabel: string;
        cancelBtnLabel: string;
        customerRequisitionLabel: string;
        customerReferenceLabel: string;
        customerMessageLabel: string;
    };
    canSubmit: boolean;
    canCancel: boolean;
    onChange(email: string): void;
    onChangeCustomerRequisition(customerRequisition: string): void;
    onChangeCustomerReference(customerReference: string): void;
    onChangeCustomerMessage(customerMessage: string): void;
    onSubmit(): void;
    onCancel(): void;
}

export interface IAddContactInfo {
    baseClassName: string;
    addFormProps: INodeProps;
    submitButton: React.ReactNode;
    cancelButton: React.ReactNode;
    inputGroupProps: INodeProps;
    label: React.ReactNode;
    error: React.ReactNode;
    input: React.ReactNode;
    customerReferenceLabel: React.ReactNode;
    customerRequisitionLabel: React.ReactNode;
    customerMessageLabel: React.ReactNode;
    customerRequisitionInput: React.ReactNode;
    customerReferenceInput: React.ReactNode;
    customerMessageInput: React.ReactNode;
    customerSupportingText: React.ReactNode;
}

/**
 * On Input change handler function.
 * @param onChange -On change input function.
 * @returns Call of onChange function.
 */
const onInputChangeHandler = (onChange: (email: string) => void) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(event.target.value || '');
};
const onCustomerRequisitionChangeHandler = (onChange: (customerRequisition: string) => void) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(event.target.value || '');
};
const onCustomerReferenceHandler = (onChange: (customerReference: string) => void) => (event: React.ChangeEvent<HTMLInputElement>): void => {
    onChange(event.target.value || '');
};
const onCustomerMessageHandler = (onChange: (customerReference: string) => void) => (event: React.ChangeEvent<HTMLTextAreaElement>): void => {
    onChange(event.target.value || '');
};

const CustomerMessageMaxLength = 150;

const getAddContactInfo = ({
    hasError,
    inputRef,
    baseClassName,
    email,
    customerReference,
    customerReferenceLocked,
    customerRequisition,
    customerRequisitionLocked,
    customerMessage,
    canSubmit,
    canCancel,
    onChange,
    onChangeCustomerRequisition,
    onChangeCustomerReference,
    onChangeCustomerMessage,
    onSubmit,
    onCancel,
    customerReferenceInputRef,
    customerRequisitionInputRef,
    customerMessageInputRef,
    resources: {
        emailLabel,
        emailErrortext,
        saveBtnLabel,
        cancelBtnLabel,
        customerMessageLabel,
        customerReferenceLabel,
        customerRequisitionLabel
    }
}: IAddContactInfoInput): IAddContactInfo => {
    const onInputChange = onInputChangeHandler(onChange);
    const onCustomerRequisitionChange = onCustomerRequisitionChangeHandler(onChangeCustomerRequisition);
    const onCustomerReferenceChange = onCustomerReferenceHandler(onChangeCustomerReference);
    const onCustomerMessageChange = onCustomerMessageHandler(onChangeCustomerMessage);

    return {
        baseClassName: `${baseClassName}__add-contact-info`,
        addFormProps: { className: 'ms-checkout-guest-profile__add-contact-info' },
        submitButton: canSubmit && (
            <Button className='ms-checkout-guest-profile__btn-save' title={saveBtnLabel} color='primary' onClick={onSubmit}>
                {saveBtnLabel}
            </Button>
        ),
        cancelButton: canCancel && (
            <Button className='ms-checkout-guest-profile__btn-cancel' title={cancelBtnLabel} color='secondary' onClick={onCancel}>
                {cancelBtnLabel}
            </Button>
        ),
        inputGroupProps: { className: classnames('ms-checkout-guest-profile__input', { 'is-invalid': hasError }) },
        label: (
            <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
                {emailLabel}
            </label>
        ),
        customerReferenceLabel: (
            <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
                {customerReferenceLabel}
            </label>
        ),
        customerRequisitionLabel: (
            <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
                {customerRequisitionLabel}
            </label>
        ),
        customerMessageLabel: (
            <label id='ms-checkout-guest-profile__label' className='ms-checkout-guest-profile__input-label'>
                {customerMessageLabel}
            </label>
        ),
        error: hasError && (
            <span id='ms-checkout-guest-profile__error' className='ms-checkout-guest-profile__input-error' role='alert' aria-live='polite'>
                {emailErrortext}
            </span>
        ),
        input: (
            <input
                ref={inputRef}
                type='text'
                className='ms-checkout-guest-profile__input-text form-control'
                aria-label={emailLabel}
                onChange={onInputChange}
                value={email}
                aria-labelledby='ms-checkout-guest-profile__label ms-checkout-guest-profile__error'
            />
        ),
        customerRequisitionInput: (
            <div className={classnames('ms-checkout-guest-profile__input-text-wrapper', { 'read-only': customerRequisitionLocked })}>
                <input
                    ref={customerRequisitionInputRef}
                    type='text'
                    className='ms-checkout-guest-profile__input-text form-control'
                    aria-label={customerRequisitionLabel}
                    onChange={customerRequisitionLocked ? undefined : onCustomerRequisitionChange}
                    value={customerRequisition}
                    maxLength={20}
                    aria-labelledby='ms-checkout-guest-profile__label ms-checkout-guest-profile__error'
                    readOnly={customerRequisitionLocked}
                />
            </div>
        ),
        customerReferenceInput: (
            <div className={classnames('ms-checkout-guest-profile__input-text-wrapper', { 'read-only': customerReferenceLocked })}>
                <input
                    ref={customerReferenceInputRef}
                    type='text'
                    className='ms-checkout-guest-profile__input-text form-control'
                    aria-label={customerReferenceLabel}
                    onChange={customerReferenceLocked ? undefined : onCustomerReferenceChange}
                    value={customerReference}
                    maxLength={60}
                    aria-labelledby='ms-checkout-guest-profile__label ms-checkout-guest-profile__error'
                    readOnly={customerReferenceLocked}
                />
            </div>
        ),
        customerMessageInput: (
            <textarea
                rows={4}
                ref={customerMessageInputRef}
                className='ms-checkout-guest-profile__input-text-area form-control'
                aria-label={customerMessageLabel}
                onChange={onCustomerMessageChange}
                value={customerMessage}
                maxLength={CustomerMessageMaxLength}
                aria-labelledby='ms-checkout-guest-profile__label ms-checkout-guest-profile__error'
            />
        ),
        customerSupportingText: (
            <span className='ms-checkout-guest-profile__customer-supporting-text'>
                {customerMessage.length ?? 0}/{CustomerMessageMaxLength}
            </span>
        )
    };
};

export default getAddContactInfo;
